// localStorage / IndexedDB のスキーマが後方非互換に更新された際に、
// 旧データを新キーへ引き継ぐ救済コードを集約するモジュール。
//
// 極めて稀に単発実行される想定なので、起動時の負荷を抑えるために
// 完了フラグと有効期限だけで早期returnし、実処理は idle 経由で走らせる。
// 詳細は同ディレクトリ `README.md` を参照。

import {
  instantFromIso,
  nowEpochMilliseconds,
} from "@nijiurachan/js/util/time";

const COMPLETION_FLAG_KEY = "nijiurachan-storage-rescue-completed-v1";

// 各救済コードの有効期限（2週間程度）。過ぎたら実行しない。
// 次に本ファイルへ手を入れるタイミングで、有効期限切れの救済コードは
// 実装ごと削除して整理する。
// `expiresAt` は正準形の ISO 8601 UTC 文字列で書く（Temporal境界を通す）。
export interface StorageRescue {
  readonly id: string;
  readonly expiresAt: string;
  run(): void;
}

function safeGetItem(key: string): string | null {
  try {
    return globalThis.localStorage?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value);
  } catch {
    // Storage拒否やquota超過は救済失敗として扱い、次回起動時に再試行する。
  }
}

function readCompletedIds(): Set<string> {
  const raw = safeGetItem(COMPLETION_FLAG_KEY);
  if (raw === null) return new Set();
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((id): id is string => typeof id === "string"));
  } catch {
    return new Set();
  }
}

function writeCompletedIds(ids: ReadonlySet<string>): void {
  safeSetItem(COMPLETION_FLAG_KEY, JSON.stringify([...ids]));
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

// UUID v1〜v8 を許容する形式判定（既存 `threadIdMigration.ts` と揃える）。
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

// ---------------------------------------------------------------------------
// 具体的な救済コード
// ---------------------------------------------------------------------------

// -- 投稿履歴 -----------------------------------------------------------------

interface LegacyPostedItem {
  boardSlug: string;
  threadId: string;
  ts: number;
}

interface PersistedPostedByBoardV1 {
  state: { posted: LegacyPostedItem[] };
  version: 1;
}

const OLD_POSTED_KEY = "nijiurachan-posted-history";
const NEW_POSTED_KEY = "nijiurachan-posted-history-by-board-v1";
const NEW_POSTED_MAX_ITEMS = 100;

// 旧 `nijiurachan-posted-history` は
// 「同じ threadId がアクセス経路の違いで重複登録される」バグを抱えていた。
// マージ時は threadId ごとに最新の `ts` を持つエントリだけを採用する。
function extractLatestLegacyPostedItems(raw: string): LegacyPostedItem[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  const rawList: unknown[] = (() => {
    if (Array.isArray(parsed)) return parsed;
    if (!isRecord(parsed)) return [];
    const state = parsed.state;
    if (isRecord(state)) {
      if (Array.isArray(state.posted)) return state.posted;
      if (Array.isArray(state.posts)) return state.posts;
    }
    return [];
  })();

  const latestByThreadId = new Map<string, LegacyPostedItem>();
  for (const item of rawList) {
    if (!isRecord(item)) continue;
    const threadId =
      typeof item.threadId === "string"
        ? item.threadId
        : typeof item.id === "string"
          ? item.id
          : "";
    if (!UUID_PATTERN.test(threadId)) continue;

    const boardSlug =
      typeof item.boardSlug === "string" && item.boardSlug.length > 0
        ? item.boardSlug
        : "aimg";

    const ts =
      typeof item.ts === "number" && Number.isFinite(item.ts)
        ? item.ts
        : typeof item.time === "number" && Number.isFinite(item.time)
          ? item.time
          : 0;

    const prior = latestByThreadId.get(threadId);
    if (!prior || ts > prior.ts) {
      latestByThreadId.set(threadId, { boardSlug, threadId, ts });
    }
  }
  return [...latestByThreadId.values()];
}

function readPersistedPostedByBoardV1(): PersistedPostedByBoardV1 {
  const raw = safeGetItem(NEW_POSTED_KEY);
  if (raw === null) return { state: { posted: [] }, version: 1 };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      parsed.version !== 1 ||
      !isRecord(parsed.state) ||
      !Array.isArray(parsed.state.posted)
    ) {
      return { state: { posted: [] }, version: 1 };
    }
    const posted = parsed.state.posted.flatMap((item): LegacyPostedItem[] => {
      if (
        !isRecord(item) ||
        typeof item.boardSlug !== "string" ||
        typeof item.threadId !== "string" ||
        typeof item.ts !== "number" ||
        !Number.isFinite(item.ts)
      ) {
        return [];
      }
      return [
        { boardSlug: item.boardSlug, threadId: item.threadId, ts: item.ts },
      ];
    });
    return { state: { posted }, version: 1 };
  } catch {
    return { state: { posted: [] }, version: 1 };
  }
}

// 事故時の救済に備えて旧 `nijiurachan-posted-history` は消さない。
// 完了フラグは残すので、再実行で二重マージは発生しない。
const postedHistoryMergeRescue: StorageRescue = {
  id: "posted-history->by-board-v1@2026-09-23",
  expiresAt: "2026-09-23T00:00:00.000Z",
  run(): void {
    const legacyRaw = safeGetItem(OLD_POSTED_KEY);
    if (legacyRaw === null) return;

    const legacyItems = extractLatestLegacyPostedItems(legacyRaw);
    if (legacyItems.length === 0) return;

    const next = readPersistedPostedByBoardV1();
    const existingThreadIds = new Set(
      next.state.posted.map((item) => item.threadId),
    );

    let added = 0;
    for (const item of legacyItems) {
      if (existingThreadIds.has(item.threadId)) continue;
      next.state.posted.push(item);
      existingThreadIds.add(item.threadId);
      added += 1;
    }
    if (added === 0) return;

    next.state.posted.sort((a, b) => b.ts - a.ts);
    if (next.state.posted.length > NEW_POSTED_MAX_ITEMS) {
      next.state.posted.length = NEW_POSTED_MAX_ITEMS;
    }
    safeSetItem(NEW_POSTED_KEY, JSON.stringify(next));
  },
};

// -- 閲覧履歴 -----------------------------------------------------------------

interface LegacyViewedItem {
  boardSlug: string;
  threadId: string;
  ts: number;
  readReplyNumber: number;
}

interface PersistedHistoryByBoardV1 {
  state: { viewed: LegacyViewedItem[] };
  version: 1;
}

const OLD_HISTORY_KEY = "nijiurachan-history";
const NEW_HISTORY_KEY = "nijiurachan-history-by-board-v1";
const NEW_VIEWED_MAX_ITEMS = 200;

function extractLatestLegacyViewedItems(raw: string): LegacyViewedItem[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }

  const rawList: unknown[] = (() => {
    if (Array.isArray(parsed)) return parsed;
    if (!isRecord(parsed)) return [];
    const state = parsed.state;
    if (isRecord(state) && Array.isArray(state.viewed)) return state.viewed;
    return [];
  })();

  const latestByThreadId = new Map<string, LegacyViewedItem>();
  for (const item of rawList) {
    if (!isRecord(item)) continue;
    const threadId =
      typeof item.threadId === "string"
        ? item.threadId
        : typeof item.id === "string"
          ? item.id
          : "";
    if (!UUID_PATTERN.test(threadId)) continue;

    const boardSlug =
      typeof item.boardSlug === "string" && item.boardSlug.length > 0
        ? item.boardSlug
        : "aimg";

    const ts =
      typeof item.ts === "number" && Number.isFinite(item.ts)
        ? item.ts
        : typeof item.time === "number" && Number.isFinite(item.time)
          ? item.time
          : 0;

    const readReplyNumber =
      typeof item.readReplyNumber === "number" &&
      Number.isFinite(item.readReplyNumber)
        ? Math.max(0, Math.trunc(item.readReplyNumber))
        : 0;

    const prior = latestByThreadId.get(threadId);
    if (!prior || ts > prior.ts) {
      latestByThreadId.set(threadId, {
        boardSlug,
        threadId,
        ts,
        readReplyNumber,
      });
    }
  }
  return [...latestByThreadId.values()];
}

function readPersistedHistoryByBoardV1(): PersistedHistoryByBoardV1 {
  const raw = safeGetItem(NEW_HISTORY_KEY);
  if (raw === null) return { state: { viewed: [] }, version: 1 };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      parsed.version !== 1 ||
      !isRecord(parsed.state) ||
      !Array.isArray(parsed.state.viewed)
    ) {
      return { state: { viewed: [] }, version: 1 };
    }
    const viewed = parsed.state.viewed.flatMap((item): LegacyViewedItem[] => {
      if (
        !isRecord(item) ||
        typeof item.boardSlug !== "string" ||
        typeof item.threadId !== "string" ||
        typeof item.ts !== "number" ||
        !Number.isFinite(item.ts) ||
        typeof item.readReplyNumber !== "number" ||
        !Number.isFinite(item.readReplyNumber)
      ) {
        return [];
      }
      return [
        {
          boardSlug: item.boardSlug,
          threadId: item.threadId,
          ts: item.ts,
          readReplyNumber: item.readReplyNumber,
        },
      ];
    });
    return { state: { viewed }, version: 1 };
  } catch {
    return { state: { viewed: [] }, version: 1 };
  }
}

// 別 id にしているのは、posted 側だけ完了フラグを持つ端末で
// viewed 側の救済をスキップしてしまうのを避けるため。
// 事故時の救済に備えて旧 `nijiurachan-history` は消さない。
const viewedHistoryMergeRescue: StorageRescue = {
  id: "viewed-history->by-board-v1@2026-09-23",
  expiresAt: "2026-09-23T00:00:00.000Z",
  run(): void {
    const legacyRaw = safeGetItem(OLD_HISTORY_KEY);
    if (legacyRaw === null) return;

    const legacyItems = extractLatestLegacyViewedItems(legacyRaw);
    if (legacyItems.length === 0) return;

    const next = readPersistedHistoryByBoardV1();
    const existingThreadIds = new Set(
      next.state.viewed.map((item) => item.threadId),
    );

    let added = 0;
    for (const item of legacyItems) {
      if (existingThreadIds.has(item.threadId)) continue;
      next.state.viewed.push(item);
      existingThreadIds.add(item.threadId);
      added += 1;
    }
    if (added === 0) return;

    next.state.viewed.sort((a, b) => b.ts - a.ts);
    if (next.state.viewed.length > NEW_VIEWED_MAX_ITEMS) {
      next.state.viewed.length = NEW_VIEWED_MAX_ITEMS;
    }
    safeSetItem(NEW_HISTORY_KEY, JSON.stringify(next));
  },
};

// ---------------------------------------------------------------------------
// 実行エンドポイント
// ---------------------------------------------------------------------------

// テストや将来の救済追加時に参照できるよう export しておく。
export const STORAGE_RESCUES: readonly StorageRescue[] = [
  postedHistoryMergeRescue,
  viewedHistoryMergeRescue,
];

export function runStorageRescues(
  rescues: readonly StorageRescue[] = STORAGE_RESCUES,
  now: number = nowEpochMilliseconds(),
): void {
  // 有効期限切れは実行対象から外す（次回リリースで実装ごと削除する）
  const applicable = rescues.filter((r) => {
    const expiresAtMs = instantFromIso(r.expiresAt)?.epochMilliseconds;
    // 不正な `expiresAt` は誤って恒久実行にしないよう、即時失効扱いにする。
    return expiresAtMs !== undefined && now <= expiresAtMs;
  });
  if (applicable.length === 0) return;

  const completed = readCompletedIds();
  const pending = applicable.filter((r) => !completed.has(r.id));
  if (pending.length === 0) return;

  let dirty = false;
  for (const rescue of pending) {
    try {
      rescue.run();
      completed.add(rescue.id);
      dirty = true;
    } catch (error) {
      // 単発の失敗で他救済まで止めない。次回起動で再試行される。
      console.warn(`storage rescue failed: ${rescue.id}`, error);
    }
  }
  if (dirty) writeCompletedIds(completed);
}
